/**
 * Regras da aba "Simular importação". Sem React, sem MUI — só cálculo, para o
 * verificador (`verifica_importacao.ts`) rodar sem abrir o navegador.
 *
 * Porte fiel da aba do painel publicado (`gen_painel_recompra.py`, 19/09/2026).
 *
 * A regra de negócio é da Mariana (19/09/2026): "na importação eu sempre compro o
 * equivalente a 4 meses de estoque … vários desses itens esgotaram muito rápido,
 * então teria que projetar como seria". Daí:
 *
 *   pedir = ritmo × (meses × 30 [+ lead time, se marcado]) − estoque de hoje
 *
 * O ritmo padrão é o ATIVO (unidades ÷ dias em que havia estoque). O giro médio do
 * painel principal divide por todos os dias desde a estreia, inclusive os em que o
 * item já estava esgotado — e subestima a demanda de quem esgotou. Para quem não
 * esgotou, os dois são iguais.
 */

import type {
  BaseRitmo,
  LinhaImportacao,
  ParamImportacao,
  PayloadImportacao,
  QtdImportacao,
} from './tipos'
import { idProduto, type Celula } from './logic'

export const PARAM_PADRAO: ParamImportacao = { meses: 4, lead: false, base: 'ativo' }

export const ROTULO_BASE: Record<BaseRitmo, string> = {
  ativo: 'Ritmo ativo (enquanto tinha estoque)',
  r30: 'Últimos 30 dias ativos',
  medio: 'Média desde a estreia (giro do painel)',
}

/** SKU com menos de 30 dias ativos: o ritmo é de lançamento, não de regime. */
export const DIAS_ATIVOS_CURTO = 30

export type MapaQtd = Record<string, QtdImportacao>

export function mapaQtd(itens: QtdImportacao[] | undefined): MapaQtd {
  const m: MapaQtd = {}
  for (const q of itens || []) m[q.id] = q
  return m
}

export const ritmoDe = (l: LinhaImportacao, base: BaseRitmo): number =>
  (base === 'r30' ? l.ritmo_30d : base === 'medio' ? l.ritmo_medio : l.ritmo_ativo) || 0

/** Dias de cobertura pedidos: meses × 30, mais o lead time se marcado. */
export const diasCobertura = (p: ParamImportacao, payload: PayloadImportacao): number =>
  Math.max(0, Number(p.meses) || 0) * payload.parametros.dias_mes +
  (p.lead ? payload.parametros.lead : 0)

export const demanda = (l: LinhaImportacao, p: ParamImportacao, payload: PayloadImportacao) =>
  Math.round(ritmoDe(l, p.base) * diasCobertura(p, payload))

/** O que o painel sugere pedir: demanda − o que ainda tem (nunca negativo). */
export const sugerido = (l: LinhaImportacao, p: ParamImportacao, payload: PayloadImportacao) =>
  Math.max(0, demanda(l, p, payload) - Math.max(0, l.estoque_hoje_estimado || 0))

/** Quantidade efetiva: a editada à mão, se houver; senão a sugerida. */
export const quantidade = (
  l: LinhaImportacao,
  p: ParamImportacao,
  payload: PayloadImportacao,
  qtds: MapaQtd,
) => {
  const q = qtds[idProduto(l.codigo)]
  return q && q.qtd != null ? Number(q.qtd) : sugerido(l, p, payload)
}

export const editada = (
  l: LinhaImportacao,
  p: ParamImportacao,
  payload: PayloadImportacao,
  qtds: MapaQtd,
) => quantidade(l, p, payload, qtds) !== sugerido(l, p, payload)

export type SituacaoImp = 'esgotado' | 'baixo' | 'estoque'

/** esgotado = estoque de hoje ≤ 0; baixo = cobertura de até 30 dias. */
export const situacao = (l: LinhaImportacao): SituacaoImp =>
  l.esgotado ? 'esgotado' : l.cobertura_hoje != null && l.cobertura_hoje <= 30 ? 'baixo' : 'estoque'

export const ROTULO_SITUACAO: Record<SituacaoImp, string> = {
  esgotado: 'Esgotou',
  baixo: 'Acaba em breve',
  estoque: 'Em estoque',
}

export const ritmoDeLancamento = (l: LinhaImportacao) => l.dias_ativos < DIAS_ATIVOS_CURTO

// --------------------------------------------------------------------- filtro e ordem

export type FiltrosImp = { q: string; fam: string; esg: boolean }
export const FILTROS_IMP_VAZIOS: FiltrosImp = { q: '', fam: '', esg: false }

export function filtrarImp(linhas: LinhaImportacao[], f: FiltrosImp): LinhaImportacao[] {
  const t = f.q.trim().toLowerCase()
  return linhas.filter(
    (l) =>
      (!t || `${l.codigo} ${l.nome}`.toLowerCase().includes(t)) &&
      (!f.fam || l.familia === f.fam) &&
      (!f.esg || l.esgotado),
  )
}

export type ChaveOrdemImp =
  | 'nome'
  | 'unidades'
  | 'estoque_hoje_estimado'
  | 'ritmo'
  | 'cobertura_hoje'
  | 'demanda'
  | 'pedir'

export type OrdemImp = { k: ChaveOrdemImp; dir: 1 | -1 }
export const ORDEM_IMP_PADRAO: OrdemImp = { k: 'pedir', dir: -1 }

export function proximaOrdemImp(atual: OrdemImp, k: ChaveOrdemImp): OrdemImp {
  if (atual.k === k) return { k, dir: (atual.dir === 1 ? -1 : 1) as 1 | -1 }
  return { k, dir: k === 'nome' ? 1 : -1 }
}

export function ordenarImp(
  arr: LinhaImportacao[],
  ord: OrdemImp,
  p: ParamImportacao,
  payload: PayloadImportacao,
  qtds: MapaQtd,
): LinhaImportacao[] {
  const valor = (l: LinhaImportacao): string | number => {
    switch (ord.k) {
      case 'pedir':
        return quantidade(l, p, payload, qtds)
      case 'demanda':
        return demanda(l, p, payload)
      case 'ritmo':
        return ritmoDe(l, p.base)
      case 'nome':
        return l.nome
      default: {
        const x = l[ord.k]
        return x == null ? (ord.dir > 0 ? 1e15 : -1e15) : x
      }
    }
  }
  return [...arr].sort((a, b) => {
    const va = valor(a)
    const vb = valor(b)
    if (typeof va === 'string' || typeof vb === 'string') {
      return String(va).localeCompare(String(vb)) * ord.dir
    }
    return (va - vb) * ord.dir
  })
}

export const totalPedir = (
  arr: LinhaImportacao[],
  p: ParamImportacao,
  payload: PayloadImportacao,
  qtds: MapaQtd,
) => arr.reduce((s, l) => s + quantidade(l, p, payload, qtds), 0)

// ------------------------------------------------------------------------- exportação

export const COLUNAS_IMPORTACAO = [
  'Código',
  'Descrição',
  'Família',
  'Quantidade a pedir',
  'Sugerido pelo painel',
  'Estoque hoje (estimado)',
  'Estoque na planilha',
  'Ritmo usado (un/dia)',
  'Base do ritmo',
  'Meses cobertos',
  'Lead time somado',
  'Vendido no período',
  'Esgotou em',
  'Última importação (OC)',
  'Qtd. última importação',
]

export function linhasExportImportacao(
  arr: LinhaImportacao[],
  p: ParamImportacao,
  payload: PayloadImportacao,
  qtds: MapaQtd,
): Celula[][] {
  const aoa: Celula[][] = [COLUNAS_IMPORTACAO]
  for (const l of arr) {
    aoa.push([
      l.codigo,
      l.nome,
      l.familia || '',
      quantidade(l, p, payload, qtds),
      sugerido(l, p, payload),
      l.estoque_hoje_estimado,
      l.estoque_planilha,
      Number(ritmoDe(l, p.base).toFixed(3)),
      p.base,
      Number(p.meses) || 0,
      p.lead ? 'sim' : 'não',
      l.unidades,
      l.esgotou_em || '',
      l.oc_ref ? String(l.oc_ref.numero ?? '') : '',
      l.oc_ref ? l.oc_ref.qtd : '',
    ])
  }
  return aoa
}

export const nomeArquivoImportacao = (p: ParamImportacao, hoje = new Date()) =>
  `importacao_${Number(p.meses) || 0}meses_${hoje.toISOString().slice(0, 10)}.csv`
